import React, { useEffect } from 'react';
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { Toaster } from 'sonner';
import '@/App.css';
import { Header } from '@/components/site/Header';
import { Footer } from '@/components/site/Footer';
import { RomildaAIWidget } from '@/components/ai/RomildaAIWidget';
import { applySeo } from '@/lib/seo';
import { LanguageProvider, useI18n } from '@/lib/i18n';
import Home from '@/pages/Home';
import Services from '@/pages/Services';
import About from '@/pages/About';
import Contact from '@/pages/Contact';
import Leads from '@/pages/Leads';
import NotFound from '@/pages/NotFound';

/** Restores scroll on route change, honours #anchor deep links, applies SEO. */
const RouteEffects = () => {
  const { pathname, hash } = useLocation();
  const { lang } = useI18n();

  useEffect(() => {
    applySeo(lang, pathname);
  }, [lang, pathname]);

  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 180);
      return () => clearTimeout(t);
    }
    window.scrollTo({ top: 0, behavior: 'auto' });
    return undefined;
  }, [pathname, hash]);

  return null;
};

const SiteLayout = ({ children }) => (
  <>
    <Header />
    <main id="main">{children}</main>
    <Footer />
    <RomildaAIWidget />
  </>
);

function App() {
  return (
    <LanguageProvider>
      <div className="App">
        <BrowserRouter>
          <RouteEffects />
          <Routes>
            <Route
              path="/"
              element={
                <SiteLayout>
                  <Home />
                </SiteLayout>
              }
            />
            <Route
              path="/services"
              element={
                <SiteLayout>
                  <Services />
                </SiteLayout>
              }
            />
            <Route
              path="/about"
              element={
                <SiteLayout>
                  <About />
                </SiteLayout>
              }
            />
            <Route
              path="/contact"
              element={
                <SiteLayout>
                  <Contact />
                </SiteLayout>
              }
            />
            <Route path="/leads" element={<Leads />} />
            <Route
              path="*"
              element={
                <SiteLayout>
                  <NotFound />
                </SiteLayout>
              }
            />
          </Routes>
        </BrowserRouter>

        <Toaster
          position="top-center"
          toastOptions={{
            style: {
              background: 'var(--rmh-surface)',
              color: 'var(--rmh-ink)',
              border: '1px solid var(--rmh-border-strong)',
              borderRadius: '12px',
              fontFamily: "'IBM Plex Sans', sans-serif",
              fontSize: '0.86rem',
            },
          }}
        />
      </div>
    </LanguageProvider>
  );
}

export default App;
